import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, NavLink, useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import './Admin.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003';

function Admin() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminKey, setAdminKey] = useState('');
  const [keyInput, setKeyInput] = useState('');
  const [error, setError] = useState('');
  const [socket, setSocket] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Close mobile menu when route changes
  const handleNavClick = () => {
    setMobileMenuOpen(false);
  };

  // Initialize socket connection
  useEffect(() => {
    const newSocket = io(API_URL);
    setSocket(newSocket);

    return () => {
      newSocket.close();
    };
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/admin/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ adminKey: keyInput })
      });

      if (response.ok) {
        setAdminKey(keyInput);
        setIsAuthenticated(true);
        localStorage.setItem('adminKey', keyInput);
      } else {
        setError('Invalid admin key');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    }
  };

  useEffect(() => {
    const storedKey = localStorage.getItem('adminKey');
    if (storedKey) {
      // Verify the stored key
      fetch(`${API_URL}/api/admin/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ adminKey: storedKey })
      })
        .then(res => {
          if (res.ok) {
            setAdminKey(storedKey);
            setIsAuthenticated(true);
          } else {
            localStorage.removeItem('adminKey');
          }
        })
        .catch(() => localStorage.removeItem('adminKey'));
    }
  }, []);

  const handleLogout = () => {
    setIsAuthenticated(false);
    setAdminKey('');
    localStorage.removeItem('adminKey');
  };

  if (!isAuthenticated) {
    return (
      <div className="admin-login">
        <div className="login-card">
          <h1>Admin Access</h1>
          <p>Enter the admin key to continue</p>
          <form onSubmit={handleLogin}>
            <input
              type="password"
              value={keyInput}
              onChange={(e) => setKeyInput(e.target.value)}
              placeholder="Admin Key"
              autoFocus
            />
            {error && <p className="error">{error}</p>}
            <button type="submit">Login</button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      {/* Mobile Header */}
      <header className="admin-mobile-header">
        <button 
          className={`hamburger-btn ${mobileMenuOpen ? 'open' : ''}`}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
        <h1>Admin Panel</h1>
      </header>

      {/* Overlay for mobile menu */}
      {mobileMenuOpen && (
        <div 
          className="mobile-menu-overlay" 
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      <aside className={`admin-sidebar ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <h2>Admin Panel</h2>
          <span className="subtitle">Celebration of Life</span>
        </div>
        <nav className="sidebar-nav">
          <NavLink 
            to="/admin" 
            end 
            className={({ isActive }) => isActive ? 'active' : ''}
            onClick={handleNavClick}
          >
            Dashboard & RSVPs
          </NavLink>
          <NavLink 
            to="/admin/condolences" 
            className={({ isActive }) => isActive ? 'active' : ''}
            onClick={handleNavClick}
          >
            Condolences
          </NavLink>
          <NavLink 
            to="/admin/event" 
            className={({ isActive }) => isActive ? 'active' : ''}
            onClick={handleNavClick}
          >
            Event Details
          </NavLink>
        </nav>
        <div className="sidebar-footer">
          <a href="/" target="_blank" rel="noopener noreferrer">View Site →</a>
          <button onClick={handleLogout}>Logout</button>
        </div>
      </aside>
      <main className="admin-main">
        <Routes>
          <Route path="/" element={<AdminDashboard adminKey={adminKey} socket={socket} />} />
          <Route path="/condolences" element={<AdminCondolences adminKey={adminKey} socket={socket} />} />
          <Route path="/event" element={<AdminEventDetails adminKey={adminKey} />} />
        </Routes>
      </main>
    </div>
  );
}

// Dashboard Component (combined with RSVPs management)
function AdminDashboard({ adminKey, socket }) {
  const [stats, setStats] = useState(null);
  const [rsvps, setRsvps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingRSVP, setEditingRSVP] = useState(null);
  const [editForm, setEditForm] = useState({
    fullName: '',
    email: '',
    totalAttendees: 0,
    emailSubscribed: false,
    hasRSVPd: false
  });
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    try {
      const [rsvpRes, condolenceRes] = await Promise.all([
        fetch(`${API_URL}/api/rsvp-summary`),
        fetch(`${API_URL}/api/condolences?limit=1`)
      ]);

      const rsvpData = await rsvpRes.json();
      const condolenceData = await condolenceRes.json();

      setStats({
        ...rsvpData.summary,
        totalCondolences: condolenceData.pagination.total
      });
      setRsvps(rsvpData.rsvps);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Socket.io real-time updates
  useEffect(() => {
    if (!socket) return;

    const handleUpdate = () => {
      fetchData();
    };

    socket.on('rsvp-update', handleUpdate);
    socket.on('new-condolence', handleUpdate);
    socket.on('condolence-update', handleUpdate);

    return () => {
      socket.off('rsvp-update', handleUpdate);
      socket.off('new-condolence', handleUpdate);
      socket.off('condolence-update', handleUpdate);
    };
  }, [socket]);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this RSVP?')) return;

    try {
      const response = await fetch(`${API_URL}/api/rsvp/${id}`, {
        method: 'DELETE',
        headers: { 'X-Admin-Key': adminKey }
      });

      if (response.ok) {
        fetchData();
      }
    } catch (error) {
      console.error('Error deleting RSVP:', error);
    }
  };

  const handleEdit = (rsvp) => {
    setEditingRSVP(rsvp);
    setEditForm({
      fullName: rsvp.fullName || '',
      email: rsvp.email || '',
      totalAttendees: rsvp.totalAttendees || 0,
      emailSubscribed: rsvp.emailSubscribed || false,
      hasRSVPd: rsvp.hasRSVPd || false
    });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const response = await fetch(`${API_URL}/api/admin/rsvp/${editingRSVP._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Key': adminKey
        },
        body: JSON.stringify(editForm)
      });

      if (response.ok) {
        setEditingRSVP(null);
        fetchData();
      } else {
        const data = await response.json();
        alert(data.message || 'Failed to update RSVP');
      }
    } catch (error) {
      console.error('Error updating RSVP:', error);
      alert('Error updating RSVP');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="admin-loading">Loading...</div>;
  }

  return (
    <div className="admin-page">
      <h1>Dashboard</h1>
      
      {/* Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card">
          <span className="stat-value">{stats?.totalRSVPs || 0}</span>
          <span className="stat-label">Total RSVPs</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{stats?.totalGuests || 0}</span>
          <span className="stat-label">Expected Guests</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{stats?.totalAttending || 0}</span>
          <span className="stat-label">Attending</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{stats?.notAttending || 0}</span>
          <span className="stat-label">Not Attending</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{stats?.totalCondolences || 0}</span>
          <span className="stat-label">Condolences</span>
        </div>
      </div>

      {/* RSVPs Section */}
      <div className="rsvps-section">
        <h2>RSVP Management</h2>
        
        <div className="table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Guests</th>
                <th>Subscribed</th>
                <th>RSVP Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rsvps.map((rsvp) => (
                <tr key={rsvp._id}>
                  <td>{rsvp.fullName}</td>
                  <td>{rsvp.email}</td>
                  <td>
                    <span className={`badge ${rsvp.totalAttendees > 0 ? 'attending' : 'not-attending'}`}>
                      {rsvp.totalAttendees}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${rsvp.emailSubscribed ? 'subscribed' : 'not-subscribed'}`}>
                      {rsvp.emailSubscribed ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td>{formatDate(rsvp.rsvpSubmittedAt)}</td>
                  <td className="actions-cell">
                    <button 
                      className="btn-edit"
                      onClick={() => handleEdit(rsvp)}
                    >
                      Edit
                    </button>
                    <button 
                      className="btn-delete"
                      onClick={() => handleDelete(rsvp._id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Modal */}
      {editingRSVP && (
        <div className="modal-overlay" onClick={() => setEditingRSVP(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Edit RSVP</h3>
            <form onSubmit={handleEditSubmit}>
              <div className="form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  value={editForm.fullName}
                  onChange={(e) => setEditForm({ ...editForm, fullName: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Total Attendees</label>
                <input
                  type="number"
                  min="0"
                  value={editForm.totalAttendees}
                  onChange={(e) => setEditForm({ ...editForm, totalAttendees: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={editForm.emailSubscribed}
                    onChange={(e) => setEditForm({ ...editForm, emailSubscribed: e.target.checked })}
                  />
                  Subscribed to email updates
                </label>
              </div>
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={editForm.hasRSVPd}
                    onChange={(e) => setEditForm({ ...editForm, hasRSVPd: e.target.checked })}
                  />
                  Has officially RSVP'd
                </label>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setEditingRSVP(null)}>
                  Cancel
                </button>
                <button type="submit" className="btn-save" disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// Condolences Management Component
function AdminCondolences({ adminKey, socket }) {
  const [condolences, setCondolences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCondolence, setEditingCondolence] = useState(null);
  const [editForm, setEditForm] = useState({
    message: '',
    photos: []
  });
  const [saving, setSaving] = useState(false);

  const fetchCondolences = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/condolences`, {
        headers: { 'X-Admin-Key': adminKey }
      });
      const data = await response.json();
      setCondolences(data);
    } catch (error) {
      console.error('Error fetching condolences:', error);
    } finally {
      setLoading(false);
    }
  }, [adminKey]);

  useEffect(() => {
    fetchCondolences();
  }, [fetchCondolences]);

  // Socket.io real-time updates
  useEffect(() => {
    if (!socket) return;

    const handleCondolenceUpdate = () => {
      fetchCondolences();
    };

    socket.on('new-condolence', handleCondolenceUpdate);
    socket.on('condolence-update', handleCondolenceUpdate);

    return () => {
      socket.off('new-condolence', handleCondolenceUpdate);
      socket.off('condolence-update', handleCondolenceUpdate);
    };
  }, [socket, fetchCondolences]);

  const handleToggleApproval = async (id, currentStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/condolences/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Key': adminKey
        },
        body: JSON.stringify({ isApproved: !currentStatus })
      });

      if (response.ok) {
        fetchCondolences();
      }
    } catch (error) {
      console.error('Error updating condolence:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this condolence?')) return;

    try {
      const response = await fetch(`${API_URL}/api/condolences/${id}`, {
        method: 'DELETE',
        headers: { 'X-Admin-Key': adminKey }
      });

      if (response.ok) {
        fetchCondolences();
      }
    } catch (error) {
      console.error('Error deleting condolence:', error);
    }
  };

  const handleEdit = (condolence) => {
    setEditingCondolence(condolence);
    setEditForm({
      message: condolence.message || '',
      photos: condolence.photos ? [...condolence.photos] : []
    });
  };

  const handleRemovePhoto = (indexToRemove) => {
    setEditForm(prev => ({
      ...prev,
      photos: prev.photos.filter((_, index) => index !== indexToRemove)
    }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    // Validate: must have message or at least one photo
    if (!editForm.message.trim() && editForm.photos.length === 0) {
      alert('Condolence must have either a message or at least one photo.');
      return;
    }

    setSaving(true);

    try {
      const response = await fetch(`${API_URL}/api/condolences/${editingCondolence._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Key': adminKey
        },
        body: JSON.stringify({
          message: editForm.message.trim() || undefined,
          photos: editForm.photos
        })
      });

      if (response.ok) {
        setEditingCondolence(null);
        fetchCondolences();
      } else {
        const data = await response.json();
        alert(data.message || 'Failed to update condolence');
      }
    } catch (error) {
      console.error('Error updating condolence:', error);
      alert('Error updating condolence');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return <div className="admin-loading">Loading...</div>;
  }

  return (
    <div className="admin-page">
      <h1>Condolences</h1>
      <p className="page-description">Manage condolence messages and photos.</p>

      <div className="condolences-grid">
        {condolences.map((condolence) => (
          <div key={condolence._id} className={`condolence-admin-card ${!condolence.isApproved ? 'hidden' : ''}`}>
            <div className="card-header">
              <div>
                <strong>{condolence.fullName}</strong>
                {condolence.isAnonymous && <span className="anonymous-badge">Anonymous</span>}
                <span className="date">{formatDate(condolence.submittedAt)}</span>
              </div>
              <span className={`status-badge ${condolence.isApproved ? 'approved' : 'hidden'}`}>
                {condolence.isApproved ? 'Visible' : 'Hidden'}
              </span>
            </div>
            
            {condolence.message && (
              <p className="card-message">{condolence.message}</p>
            )}
            
            {condolence.photos && condolence.photos.length > 0 && (
              <div className="card-photos">
                {condolence.photos.map((photo, index) => (
                  <img key={index} src={photo.url} alt={`Photo ${index + 1}`} />
                ))}
              </div>
            )}
            
            <div className="card-actions">
              <button
                className="btn-edit"
                onClick={() => handleEdit(condolence)}
              >
                Edit
              </button>
              <button
                className={`btn-toggle ${condolence.isApproved ? 'hide' : 'show'}`}
                onClick={() => handleToggleApproval(condolence._id, condolence.isApproved)}
              >
                {condolence.isApproved ? 'Hide' : 'Show'}
              </button>
              <button
                className="btn-delete"
                onClick={() => handleDelete(condolence._id)}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Edit Condolence Modal */}
      {editingCondolence && (
        <div className="modal-overlay" onClick={() => setEditingCondolence(null)}>
          <div className="modal-content condolence-edit-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Edit Condolence</h3>
            <p className="modal-subtitle">From: <strong>{editingCondolence.fullName}</strong></p>
            
            <form onSubmit={handleEditSubmit}>
              <div className="form-group">
                <label>Message</label>
                <textarea
                  value={editForm.message}
                  onChange={(e) => setEditForm({ ...editForm, message: e.target.value })}
                  rows={5}
                  placeholder="Enter condolence message..."
                />
              </div>

              {editForm.photos.length > 0 && (
                <div className="form-group">
                  <label>Photos ({editForm.photos.length})</label>
                  <div className="edit-photos-grid">
                    {editForm.photos.map((photo, index) => (
                      <div key={index} className="edit-photo-item">
                        <img src={photo.url} alt={`Photo ${index + 1}`} />
                        <button
                          type="button"
                          className="photo-remove-btn"
                          onClick={() => handleRemovePhoto(index)}
                          title="Remove photo"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!editForm.message.trim() && editForm.photos.length === 0 && (
                <p className="validation-warning">
                  ⚠️ Condolence must have either a message or at least one photo.
                </p>
              )}

              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setEditingCondolence(null)}>
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn-save" 
                  disabled={saving || (!editForm.message.trim() && editForm.photos.length === 0)}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// Event Details Management Component
function AdminEventDetails({ adminKey }) {
  const [eventDetails, setEventDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    fetchEventDetails();
  }, []);

  const fetchEventDetails = async () => {
    try {
      const response = await fetch(`${API_URL}/api/event-details`);
      const data = await response.json();
      setEventDetails(data);
    } catch (error) {
      console.error('Error fetching event details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      const response = await fetch(`${API_URL}/api/event-details`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Key': adminKey
        },
        body: JSON.stringify(eventDetails)
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Event details saved successfully!' });
      } else {
        const data = await response.json();
        setMessage({ type: 'error', text: data.message || 'Failed to save' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Connection error' });
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setEventDetails(prev => ({ ...prev, [field]: value }));
  };

  const updateLocation = (field, value) => {
    setEventDetails(prev => ({
      ...prev,
      location: { ...prev.location, [field]: value }
    }));
  };

  const updateSchedule = (index, field, value) => {
    setEventDetails(prev => {
      const newSchedule = [...prev.schedule];
      newSchedule[index] = { ...newSchedule[index], [field]: value };
      return { ...prev, schedule: newSchedule };
    });
  };

  if (loading) {
    return <div className="admin-loading">Loading...</div>;
  }

  return (
    <div className="admin-page">
      <h1>Event Details</h1>
      <p className="page-description">Update the event information displayed on the website.</p>

      <form onSubmit={handleSubmit} className="event-form">
        <div className="form-section">
          <h3>Basic Information</h3>
          
          <div className="form-group">
            <label>Person's Name</label>
            <input
              type="text"
              value={eventDetails?.personName || ''}
              onChange={(e) => updateField('personName', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>Event Date</label>
            <input
              type="date"
              value={eventDetails?.eventDate?.split('T')[0] || ''}
              onChange={(e) => updateField('eventDate', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>Event Time</label>
            <input
              type="text"
              value={eventDetails?.eventTime || ''}
              onChange={(e) => updateField('eventTime', e.target.value)}
              placeholder="e.g., 3pm - 9pm"
            />
          </div>
        </div>

        <div className="form-section">
          <h3>Location</h3>
          
          <div className="form-group">
            <label>Venue Name</label>
            <input
              type="text"
              value={eventDetails?.location?.name || ''}
              onChange={(e) => updateLocation('name', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>Address</label>
            <input
              type="text"
              value={eventDetails?.location?.address || ''}
              onChange={(e) => updateLocation('address', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>Google Maps Link</label>
            <input
              type="url"
              value={eventDetails?.location?.googleMapsLink || ''}
              onChange={(e) => updateLocation('googleMapsLink', e.target.value)}
              placeholder="https://maps.google.com/..."
            />
          </div>
        </div>

        <div className="form-section">
          <h3>Schedule</h3>
          
          {eventDetails?.schedule?.map((item, index) => (
            <div key={index} className="schedule-item">
              <input
                type="text"
                value={item.time}
                onChange={(e) => updateSchedule(index, 'time', e.target.value)}
                placeholder="Time"
                className="time-input"
              />
              <input
                type="text"
                value={item.description}
                onChange={(e) => updateSchedule(index, 'description', e.target.value)}
                placeholder="Description"
                className="description-input"
              />
            </div>
          ))}
        </div>

        <div className="form-section">
          <h3>Obituary</h3>
          
          <div className="form-group">
            <label>Obituary Text</label>
            <textarea
              value={eventDetails?.obituary || ''}
              onChange={(e) => updateField('obituary', e.target.value)}
              rows={10}
              placeholder="Write about the person being remembered..."
            />
          </div>
        </div>

        {message && (
          <div className={`form-message ${message.type}`}>
            {message.text}
          </div>
        )}

        <button type="submit" className="btn-save" disabled={saving}>
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </form>
    </div>
  );
}

export default Admin;


